import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import React from 'react'
import {
  getOnDemandSecondaryImage,
  OnDemandCityConfig
} from 'src/utils/onDemand'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

interface IProps {
  /** When set, the city's coverage zones render as chips. When unset (parent
   *  page), this section renders a generic "ask us" callout instead. */
  coverage?: OnDemandCityConfig
}

/**
 * Coverage-zones chip list with a "¿Tu zona no aparece?" WhatsApp escape
 * hatch. Editorial vocabulary — 12-col rail (left photo, right chips),
 * hairline-bordered pills, no shadows.
 *
 * The left photo is `getOnDemandSecondaryImage()` — a fleet shot no other
 * section on a city page uses (see the allocation table on
 * `ON_DEMAND_PHOTOS`).
 */
const CoverageZones = ({ coverage }: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const cityDisplayName = coverage?.displayName
  const zones = coverage?.coverageZones ?? []

  const whatsappMessage = cityDisplayName
    ? t('HERO.WHATSAPP_MSG_CITY', { city: cityDisplayName })
    : t('HERO.WHATSAPP_MSG')
  const whatsappUrl = buildWhatsappUrl(whatsappMessage)

  if (zones.length === 0) return null

  const photoSrc = getOnDemandSecondaryImage()
  const photoAlt = cityDisplayName
    ? t('COVERAGE.PHOTO_ALT_CITY', { city: cityDisplayName })
    : t('COVERAGE.PHOTO_ALT')

  return (
    <section
      aria-labelledby="coverage-heading"
      className="border-t border-ink-100/15 bg-white-500 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 md:grid md:grid-cols-12 md:items-center md:gap-12 md:px-10">
        <div className="md:col-span-5">
          {/* Mobile keeps portrait framing (works in the stack); desktop
              switches to a shorter landscape ratio so the photo doesn't
              tower over the heading + chip list on the right rail. */}
          <div className="relative aspect-4/5 w-full overflow-hidden rounded-lg bg-blue-500/10 md:aspect-4/3">
            <Image
              src={photoSrc}
              alt={photoAlt}
              fill
              sizes="(max-width: 900px) 100vw, 40vw"
              className="object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="mt-8 md:col-span-7 md:mt-0">
          <h2
            id="coverage-heading"
            className="max-w-3xl font-display text-[24px] leading-[1.2] font-medium tracking-[-0.01em] text-ink-700 md:text-[32px]"
          >
            {cityDisplayName
              ? t('COVERAGE.TITLE_CITY', { city: cityDisplayName })
              : t('COVERAGE.TITLE')}
          </h2>

          <ul className="mt-8 flex flex-wrap gap-2">
            {zones.map((zone) => (
              <li
                key={zone}
                className="rounded-full border border-ink-100/15 bg-cream-300 px-3.5 py-1.5 text-[14px] text-ink-700"
              >
                {zone}
              </li>
            ))}
          </ul>

          <p className="mt-8 text-[15px] leading-relaxed text-ink-200 md:text-base">
            {`${t('COVERAGE.MORE_AREAS_QUESTION')} `}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline-offset-4 hover:underline"
            >
              {t('COVERAGE.MORE_AREAS_CTA')}
            </a>
          </p>
        </div>
      </div>
    </section>
  )
}

export default React.memo(CoverageZones)
